import { useMemo, useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Settings,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  LogOut,
  Key,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { TenantSwitcherItems } from './tenant-switcher'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/auth-context'
import { useTheme } from '@/contexts/theme-context'
import { api } from '@/services/api'
import { navGroups, pagesNavGroup, type NavItem } from './nav-items'
import { usePinnedPages } from '@/lib/pinned-pages'
import { activeSession } from '@/services/nomos/sessions'
import { SentinelMark } from './sentinel-mark'

/** The signed-in organisation's display name (the slug when NomOS gave no name). */
function organisationName(): string {
  const s = activeSession()
  return s?.tenant_name?.trim() || s?.slug || ''
}


interface SidebarProps {
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  onLogout?: () => void
  /** Called after a nav link is clicked — used by mobile to close the drawer */
  onNavClick?: () => void
  className?: string
  /** Mobile-only: whether the drawer is currently open */
  mobileOpen?: boolean
  /** Whether we are in mobile (< 768 px) context */
  isMobile?: boolean
}

export function Sidebar({ collapsed, onCollapsedChange, onLogout, onNavClick, className, mobileOpen, isMobile }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasRole, isAdmin } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()

  const [userInfo, setUserInfo] = useState<{ email: string; role: string } | null>(null)

  useEffect(() => {
    api.getCurrentUser()
      .then((user) => setUserInfo({ email: user.email, role: user.role }))
      .catch(() => {/* ignore */})
  }, [])

  const getInitials = (email: string): string => {
    const name = email.split('@')[0]
    const parts = name.split(/[._-]/).filter(p => p.length > 0)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.replace(/[._-]/g, '').slice(0, 2).toUpperCase() || 'SA'
  }

  const handleLogout = () => {
    api.logout()
    onLogout?.()
    navigate('/login')
  }

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const resetPasswordForm = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setShowPassword(false)
    setPasswordError(null)
    setPasswordSuccess(false)
  }

  const handleChangePassword = async () => {
    setPasswordError(null)
    setPasswordSuccess(false)

    if (!currentPassword) {
      setPasswordError('Current password is required')
      return
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setIsChangingPassword(true)
    try {
      await api.changePassword(currentPassword, newPassword)
      setPasswordSuccess(true)
      setTimeout(() => {
        setShowChangePassword(false)
        resetPasswordForm()
      }, 1500)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password'
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } }
        setPasswordError(axiosErr.response?.data?.detail || errorMessage)
      } else {
        setPasswordError(errorMessage)
      }
    } finally {
      setIsChangingPassword(false)
    }
  }

  // Filter nav items by role (SEN-261)
  const filterByRole = useMemo(() => {
    return (items: NavItem[]) =>
      items.filter((item) => !item.minRole || isAdmin || hasRole(item.minRole))
  }, [hasRole, isAdmin])

  const orgName = organisationName()
  const pinned = pagesNavGroup(usePinnedPages())
  const roleGroups = navGroups
    .map((g) => ({ label: g.label, items: filterByRole(g.items) }))
    .filter((g) => g.items.length > 0)
  // pinned pages sit under the first group (Sentinel, Dashboard, …), above the Ontology
  const visibleGroups = pinned ? [roleGroups[0], pinned, ...roleGroups.slice(1)] : roleGroups

  // On mobile the aside is position:fixed and bypasses any wrapper offset,
  // so we drive its left position directly via framer-motion animate.
  const mobileLeft = isMobile ? (mobileOpen ? 0 : -(collapsed ? 72 : 240)) : 0

  return (
    <>
    <motion.aside
      initial={isMobile ? { left: -(collapsed ? 72 : 240) } : false}
      animate={isMobile ? { left: mobileLeft, width: collapsed ? 72 : 240 } : { width: collapsed ? 72 : 240 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={cn(
        'fixed left-0 top-0 z-40 h-screen flex flex-col',
        'bg-sidebar border-r border-sidebar-border',
        className
      )}
    >
      {/* The Sentinel mark, then the signed-in organisation's name (owner, 2026-10-21). */}
      <div className="flex h-16 items-center px-4 border-b border-sidebar-border">
        <NavLink to="/dashboard" className="flex min-w-0 items-center gap-3" title={orgName}>
          <SentinelMark className="h-9 w-9 flex-shrink-0 rounded-lg [&>svg]:h-5 [&>svg]:w-5" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="truncate font-semibold text-sidebar-foreground whitespace-nowrap overflow-hidden"
              >
                {orgName}
              </motion.span>
            )}
          </AnimatePresence>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col">
        {visibleGroups.map((group, i) => {
          const last = i === visibleGroups.length - 1 && visibleGroups.length > 1
          return (
            <div key={group.items[0].href} className={cn(i > 0 && 'mt-4', last && 'mt-auto')}>
              {i > 0 && <Separator className="mb-4 bg-sidebar-border" />}
              <AnimatePresence>
                {group.label && !collapsed && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="px-3 mb-2 text-xs font-medium text-sidebar-foreground/60 uppercase tracking-wider"
                  >
                    {group.label}
                  </motion.p>
                )}
              </AnimatePresence>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavItemComponent
                    key={item.href}
                    item={item}
                    collapsed={collapsed}
                    isActive={location.pathname === item.href || location.pathname.startsWith(item.href + '/')}
                    onNavClick={onNavClick}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </nav>

      {/* Bottom section: theme toggle + user profile + collapse */}
      <div className="border-t border-sidebar-border p-3 space-y-2">
        {/* Theme toggle */}
        {collapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={() => setTheme(resolvedTheme === 'light' ? 'dark' : 'light')}
                className="flex items-center justify-center w-full py-2 rounded-lg text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
              >
                {resolvedTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Toggle theme</TooltipContent>
          </Tooltip>
        ) : (
          <button
            onClick={() => setTheme(resolvedTheme === 'light' ? 'dark' : 'light')}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            {resolvedTheme === 'light' ? <Moon className="h-4 w-4 flex-shrink-0" /> : <Sun className="h-4 w-4 flex-shrink-0" />}
            <span>{resolvedTheme === 'light' ? 'Dark mode' : 'Light mode'}</span>
          </button>
        )}

        {/* User profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button className="flex items-center justify-center w-full py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                        {userInfo ? getInitials(userInfo.email) : 'SA'}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{userInfo?.email || 'Account'}</TooltipContent>
              </Tooltip>
            ) : (
              <button className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors">
                <Avatar className="h-7 w-7 flex-shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                    {userInfo ? getInitials(userInfo.email) : 'SA'}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{userInfo?.email?.split('@')[0] || 'Account'}</span>
              </button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium capitalize">{userInfo?.role || 'Admin'}</p>
                <p className="text-xs text-muted-foreground">{userInfo?.email || 'admin@sentinel.local'}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <TenantSwitcherItems onLeave={(intent) => { onLogout?.(); navigate('/login', { state: intent }) }} />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
              <Key className="mr-2 h-4 w-4" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCollapsedChange(!collapsed)}
          className={cn(
            'w-full justify-center text-sidebar-foreground/60 hover:text-sidebar-foreground',
            'hover:bg-sidebar-accent'
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </Button>
      </div>
    </motion.aside>

    {/* Change Password Dialog */}
    <Dialog
      open={showChangePassword}
      onOpenChange={(open) => {
        if (!open) {
          setShowChangePassword(false)
          resetPasswordForm()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Change Password
          </DialogTitle>
          <DialogDescription>
            Enter your current password and choose a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <div className="relative">
              <Input
                id="current-password"
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-new-password">Confirm New Password</Label>
            <Input
              id="confirm-new-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>

          {passwordError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="w-4 h-4" />
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Key className="w-4 h-4" />
              Password changed successfully!
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            Password must be at least 8 characters long.
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowChangePassword(false)
              resetPasswordForm()
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
          >
            {isChangingPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}

// Sidebar width constants for use by parent components
export const SIDEBAR_WIDTH = 240
export const SIDEBAR_WIDTH_COLLAPSED = 72

interface NavItemProps {
  item: NavItem
  collapsed: boolean
  isActive: boolean
  onNavClick?: () => void
}

function NavItemComponent({ item, collapsed, isActive, onNavClick }: NavItemProps) {
  const Icon = item.icon
  return item.useCount
    ? <CountedNavItem item={item} collapsed={collapsed} isActive={isActive} onNavClick={onNavClick} />
    : <NavItemBody item={item} collapsed={collapsed} isActive={isActive} onNavClick={onNavClick} count={null} icon={Icon} />
}

function CountedNavItem(props: NavItemProps) {
  const count = props.item.useCount!()
  return <NavItemBody {...props} count={count} icon={props.item.icon} />
}

function NavItemBody({ item, collapsed, isActive, onNavClick, count, icon: Icon }: NavItemProps & {
  count: number | null; icon: NavItem['icon'] }) {

  const linkContent = (
    <NavLink
      to={item.href}
      onClick={onNavClick}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium',
        'transition-colors duration-150',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
      )}
    >
      <span className="relative flex-shrink-0">
        <Icon className={cn('h-5 w-5', isActive && 'text-sidebar-primary')} />
        {collapsed && count ? <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" aria-hidden /> : null}
      </span>
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.15 }}
            className="whitespace-nowrap overflow-hidden"
          >
            {item.title}
          </motion.span>
        )}
      </AnimatePresence>
      {!collapsed && count ? (
        <span data-testid={`nav-count-${item.href}`} className="ml-auto rounded-full bg-sidebar-accent px-1.5 text-xs tabular-nums text-sidebar-foreground/80">{count}</span>
      ) : null}
    </NavLink>
  )

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.title}
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}
